/**
 * Search builder — per-kind condition editors. One component that switches on
 * `condition.kind` and renders the right compact controls, composing the shared
 * filter primitives (FilterMultiSelect, fieldCls) and the ProfileKeyPicker tag
 * combobox. Every change flows up via onChange(next) — the builder debounces
 * the preview recount.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../../services/api';
import type { CrmForm, FormQuestionDef, SearchCondition, StudyOption, TeamMember } from '../../types';
import { studyInternalLabel } from '../../types';
import {
  DISPOSITION_REASONS, INCOMING_ELIGIBILITY_OPTIONS, LIFECYCLES, MILESTONE_FILTER_OPTIONS, MILESTONE_LABELS,
  formSurface,
} from '../../lib';
import { FilterMultiSelect, fieldCls } from '../filters';
import ProfileKeyPicker, { loadProfileKeys } from '../forms/ProfileKeyPicker';
import {
  AI_REVIEW_RESULT_OPTIONS, COMM_CHANNEL_OPTIONS, DISPOSITION_REASON_OPTIONS, DISPOSITION_STATUS_OPTIONS,
  DQ_CATEGORY_OPTIONS, NUMERIC_OPS, OP_LABELS, SPINE_STAGES, STUDY_ELIGIBILITY_VERDICT_OPTIONS,
  UTM_FIELD_OPTIONS, VITAL_PRESET_OPTIONS, formOptionLabel, groupFormsForPicker, useQuestionLabels,
} from '../../pages/search/searchLib';

// Session cache: form id -> definition questions (ported from the retired
// FormAnswersFilter — the editors unmount as steps collapse).
const questionsCache = new Map<string, FormQuestionDef[]>();

// Session cache: lab tag/test -> unit distribution (§8 mixed-unit honesty).
const labUnitsCache = new Map<string, { unit: string; count: number }[]>();

// Session cache: the future-study interest list catalog (one fetch per session).
let interestCatsCache: { key: string; label: string }[] | null = null;

/** Multi-select over the future-study interest catalog (stored by KEY —
 *  readable in saved definitions; the backend resolves keys or ids). */
function InterestListsSelect({ condition: c, onChange }: {
  condition: SearchCondition; onChange: (n: SearchCondition) => void;
}) {
  const [cats, setCats] = useState<{ key: string; label: string }[]>(interestCatsCache || []);
  useEffect(() => {
    if (interestCatsCache) return;
    let alive = true;
    api.get('/api/crm/interest-categories')
      .then((r) => {
        const list = (r.data?.categories || []).map((x: { key: string; label: string }) => ({ key: x.key, label: x.label }));
        interestCatsCache = list;
        if (alive) setCats(list);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return (
    <FilterMultiSelect
      values={c.lists || []}
      onChange={(v) => onChange({ ...c, lists: v })}
      options={[
        { value: '', label: 'Any list' },
        // Preserve values from a saved definition the catalog no longer names.
        ...(c.lists || [])
          .filter((v) => !cats.some((x) => x.key === v))
          .map((v) => ({ value: v, label: v })),
        ...cats.map((x) => ({ value: x.key, label: x.label })),
      ]}
      label="Future-study lists"
    />
  );
}

/** "mg/dL (61) · mmol/L (12)" line under the lab editor — values compare raw,
 *  so the user must SEE when a test mixes units. */
function LabUnitsHint({ tag, testName }: { tag?: string; testName?: string }) {
  const key = (tag || '').trim() ? `t:${(tag || '').trim()}` : `n:${(testName || '').trim().toLowerCase()}`;
  const active = key.length > 2;
  const [units, setUnits] = useState<{ unit: string; count: number }[] | null>(
    active ? labUnitsCache.get(key) || null : null
  );

  useEffect(() => {
    if (!active) { setUnits(null); return; }
    const cached = labUnitsCache.get(key);
    if (cached) { setUnits(cached); return; }
    let alive = true;
    const timer = setTimeout(() => {
      api.get('/api/crm/search/lab-units', {
        params: (tag || '').trim() ? { tag: (tag || '').trim() } : { test_name: (testName || '').trim() },
      })
        .then((r) => {
          const list = r.data?.units || [];
          labUnitsCache.set(key, list);
          if (alive) setUnits(list);
        })
        .catch(() => { if (alive) setUnits(null); });
    }, 400);
    return () => { alive = false; clearTimeout(timer); };
  }, [key, active, tag, testName]);

  if (!active || !units || !units.length) return null;
  const mixed = units.length > 1;
  return (
    <p className={`text-xs ${mixed ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>
      Units on record: {units.slice(0, 4).map((u) => `${u.unit} (${u.count})`).join(' · ')}
      {mixed ? ' — values are compared raw, not converted' : ''}
    </p>
  );
}

const NUM_TYPES = new Set(['number', 'scale', 'calculation', 'height']);

/** Operator vocabulary per question type (raw keys get the full legacy set). */
function answerOpsForType(type: string | undefined): { value: string; label: string }[] {
  const ops = (values: string[]) => values.map((v) => ({ value: v, label: OP_LABELS[v] || v }));
  if (!type) return ops(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'contains', 'answered']);
  if (type === 'yes_no' || type === 'single_choice') return ops(['eq', 'neq', 'answered']);
  if (type === 'multiple_choice') return ops(['includes', 'not_includes', 'answered']);
  if (NUM_TYPES.has(type)) return ops(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'answered']);
  if (type === 'date') return ops(['eq', 'neq', 'contains', 'answered']);
  return ops(['contains', 'eq', 'neq', 'answered']);
}

interface Props {
  condition: SearchCondition;
  onChange: (next: SearchCondition) => void;
  studies: StudyOption[];
  team: TeamMember[];
  forms: CrmForm[];
}

const rowCls = 'flex flex-wrap items-center gap-1.5';
const hintCls = 'text-xs text-gray-400 dark:text-gray-500';
const checkCls = 'rounded border-gray-300 text-primary-600 focus:ring-primary-500';
const labelCls = 'flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300';

/** Numeric op + value(+value2) + optional recency, shared by lab/vital. */
function NumericValueRow({ condition: c, onChange, allowRecency = true }: {
  condition: SearchCondition; onChange: (n: SearchCondition) => void; allowRecency?: boolean;
}) {
  return (
    <>
      <div className={rowCls}>
        <select
          value={c.op || ''}
          onChange={(e) => onChange({ ...c, op: e.target.value || undefined, ...(e.target.value ? {} : { value: '', value2: '' }) })}
          aria-label="Value comparison"
          className={`${fieldCls} w-auto cursor-pointer`}
        >
          <option value="">has any value</option>
          {NUMERIC_OPS.map((op) => <option key={op} value={op}>{OP_LABELS[op]}</option>)}
        </select>
        {c.op && (
          <input
            type="number"
            value={String(c.value ?? '')}
            onChange={(e) => onChange({ ...c, value: e.target.value })}
            placeholder="value"
            aria-label="Value"
            className={`${fieldCls} w-24`}
          />
        )}
        {c.op === 'between' && (
          <>
            <span className={hintCls}>and</span>
            <input
              type="number"
              value={String(c.value2 ?? '')}
              onChange={(e) => onChange({ ...c, value2: e.target.value })}
              placeholder="value"
              aria-label="Upper value"
              className={`${fieldCls} w-24`}
            />
          </>
        )}
      </div>
      {allowRecency && (
        <div className={rowCls}>
          <span className={hintCls}>measured within</span>
          <input
            type="number"
            min={1}
            value={String(c.within_days ?? '')}
            onChange={(e) => onChange({ ...c, within_days: e.target.value === '' ? undefined : e.target.value })}
            placeholder="any time"
            aria-label="Within days"
            className={`${fieldCls} w-24`}
          />
          <span className={hintCls}>days</span>
          <label className={`${labelCls} ml-2`}>
            <input
              type="checkbox"
              checked={c.require_verified === true}
              onChange={(e) => onChange({ ...c, require_verified: e.target.checked || undefined })}
              className={checkCls}
            />
            verified only
          </label>
        </div>
      )}
    </>
  );
}

/** Tag-or-name picker shared by condition/medication. */
function TagOrNameEditor({ condition: c, onChange, keyPrefixes, statuses, placeholder }: {
  condition: SearchCondition; onChange: (n: SearchCondition) => void;
  keyPrefixes: string[]; statuses: { value: string; label: string }[]; placeholder: string;
}) {
  const mode = (c.name || '').trim() && !(c.tag || '').trim() ? 'name' : 'tag';
  const [useName, setUseName] = useState(mode === 'name');
  return (
    <div className="space-y-1.5">
      {useName ? (
        <input
          value={c.name || ''}
          onChange={(e) => onChange({ ...c, name: e.target.value, tag: '', tag_label: undefined })}
          placeholder={placeholder}
          aria-label="Name"
          className={`${fieldCls} w-full`}
        />
      ) : (
        <ProfileKeyPicker
          value={c.tag || null}
          onChange={async (key) => {
            // Keep the tag's words beside its key (as the lab editor does) so the step reads
            // "Has "Type 2 diabetes"", not the raw field tag.
            if (!key) { onChange({ ...c, tag: '', tag_label: undefined, name: '' }); return; }
            let label: string | undefined;
            try { label = (await loadProfileKeys()).find((k) => k.key === key)?.label; } catch { /* label optional */ }
            onChange({ ...c, tag: key, tag_label: label, name: '' });
          }}
          placeholder={`Pick a field tag (${keyPrefixes.join(', ')}…)`}
        />
      )}
      <div className={rowCls}>
        <button
          type="button"
          onClick={() => setUseName((v) => !v)}
          className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
        >
          {useName ? 'Use a field tag instead' : 'Type a name instead'}
        </button>
        <FilterMultiSelect
          values={c.statuses || []}
          onChange={(v) => onChange({ ...c, statuses: v.length ? v : ['active'] })}
          options={statuses}
          label="Status"
          className="ml-auto"
        />
        <label className={labelCls}>
          <input
            type="checkbox"
            checked={c.require_verified === true}
            onChange={(e) => onChange({ ...c, require_verified: e.target.checked || undefined })}
            className={checkCls}
          />
          verified only
        </label>
      </div>
    </div>
  );
}

/**
 * The form picker (Answer, Answered question, Form submitted): Landing pages and Texted forms in
 * two groups, each by volume, named the way a recruiter knows them (plan §A5). A saved search whose
 * form has since left the list keeps a readable option so the select never shows a blank.
 */
function FormSelect({ value, onChange, forms, staleTitle, ariaLabel = 'Form' }: {
  value: string; onChange: (formId: string) => void; forms: CrmForm[]; staleTitle?: string | null; ariaLabel?: string;
}) {
  const { landing, texted } = useMemo(() => groupFormsForPicker(forms), [forms]);
  const missing = Boolean(value) && !forms.some((f) => f.id === value);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel} className={`${fieldCls} w-full cursor-pointer`}>
      <option value="">Choose a form…</option>
      {missing && <option value={value}>{staleTitle || 'A form that is no longer listed'}</option>}
      {landing.length > 0 && (
        <optgroup label="Landing pages">
          {landing.map((f) => <option key={f.id} value={f.id}>{formOptionLabel(f)}</option>)}
        </optgroup>
      )}
      {texted.length > 0 && (
        <optgroup label="Texted forms">
          {texted.map((f) => <option key={f.id} value={f.id}>{formOptionLabel(f)}</option>)}
        </optgroup>
      )}
    </select>
  );
}

/** A condition with its question (and everything that described it) cleared — a new form was picked. */
function withoutQuestion(c: SearchCondition, presenceOnly?: boolean): SearchCondition {
  const next: SearchCondition = { ...c, question: '' };
  delete next.type;
  delete next.label;
  delete next.valueLabel;
  if (!presenceOnly) {
    next.op = 'eq';
    next.value = '';
  }
  return next;
}

/**
 * Answer criterion editor: pick a form → its real question list → typed op/value. Picking a form
 * pins the step to answers given ON that form by default (plan §A4 — "the natural path means what
 * it says"); unticking widens it to the same question on every form that asks it. The raw
 * question-key box is still there for keys no form lists, behind "Advanced: type a question ID".
 */
function AnswerEditor({ condition: c, onChange, forms, presenceOnly }: {
  condition: SearchCondition; onChange: (n: SearchCondition) => void; forms: CrmForm[]; presenceOnly?: boolean;
}) {
  // Reopen on the form the question was picked from — the pin, else the remembered picker form.
  const initialForm = c.form_id || c.picker_form_id || '';
  const [pickerForm, setPickerForm] = useState(initialForm);
  const [custom, setCustom] = useState(() => !initialForm && Boolean((c.question || '').trim()));
  // A search saved before the picker form was remembered opens in the ID box; once its question's
  // words arrive, reopen it on a form that asks it. Only until the user takes the wheel.
  const autoReopen = useRef(!initialForm && Boolean((c.question || '').trim()));
  const [questions, setQuestions] = useState<FormQuestionDef[] | null>(pickerForm ? questionsCache.get(pickerForm) || null : null);
  const [loading, setLoading] = useState(false);

  // Look the key up in form mode (the "N forms ask it" count) and while waiting to reopen — never
  // per keystroke in the ID box.
  const lookup = c.question && (!custom || autoReopen.current) ? [c.question] : [];
  const labelInfo = useQuestionLabels(lookup)[c.question || ''] || null;

  useEffect(() => {
    if (!autoReopen.current || !labelInfo || !labelInfo.forms.length) return;
    autoReopen.current = false;
    const listed = labelInfo.forms.find((f) => forms.some((x) => x.id === f.id)) || labelInfo.forms[0];
    setPickerForm(listed.id);
    setCustom(false);
  }, [labelInfo, forms]);

  useEffect(() => {
    if (!pickerForm) { setQuestions(null); return; }
    const cached = questionsCache.get(pickerForm);
    if (cached) { setQuestions(cached); return; }
    let alive = true;
    setLoading(true);
    api.get(`/api/crm/forms/${pickerForm}`)
      .then((r) => {
        const qs: FormQuestionDef[] = r.data?.form?.definition?.questions || [];
        questionsCache.set(pickerForm, qs);
        if (alive) setQuestions(qs);
      })
      .catch(() => { if (alive) setQuestions([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [pickerForm]);

  const selected = (questions || []).find((q) => q.key === c.question);
  const qType = selected?.type || c.type || labelInfo?.type || undefined;
  const qOptions = selected?.options?.length ? selected.options : labelInfo?.options?.length ? labelInfo.options : null;
  const ops = answerOpsForType(qType);
  const numeric = ['gt', 'gte', 'lt', 'lte'].includes(c.op || '')
    || (qType && NUM_TYPES.has(qType) && c.op !== 'answered');

  const pickForm = (formId: string) => {
    autoReopen.current = false;
    setPickerForm(formId);
    const f = forms.find((x) => x.id === formId);
    // A new form clears the old question (it may not exist there) and pins to the new form.
    onChange({
      ...withoutQuestion(c, presenceOnly),
      form_id: formId || undefined,
      picker_form_id: formId || undefined,
      picker_form_title: f?.title || undefined,
      picker_form_surface: f ? formSurface(f.surface) : undefined,
    });
  };

  const pickQuestion = (key: string) => {
    const q = (questions || []).find((x) => x.key === key);
    const next: SearchCondition = { ...c, question: key, value: '' };
    delete next.valueLabel;
    if (q) {
      next.type = q.type;
      next.label = q.label || q.key;
      next.op = presenceOnly ? undefined : answerOpsForType(q.type)[0].value;
    } else {
      delete next.type;
      delete next.label;
    }
    if (presenceOnly) { delete next.op; delete next.value; }
    onChange(next);
  };

  const switchToCustomKey = () => {
    autoReopen.current = false;
    setCustom(true);
    setPickerForm('');
    // The ID box has no form: drop the pin and the remembered form, keep the question and its words.
    onChange({ ...c, form_id: undefined, picker_form_id: undefined, picker_form_title: undefined, picker_form_surface: undefined });
  };

  const valueControl = () => {
    if (presenceOnly || c.op === 'answered') return null;
    if (qType === 'yes_no') {
      return (
        <select value={String(c.value ?? '')} onChange={(e) => onChange({ ...c, value: e.target.value })} aria-label="Answer value" className={`${fieldCls} w-full cursor-pointer`}>
          <option value="">Pick an answer…</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );
    }
    if ((qType === 'single_choice' || qType === 'multiple_choice') && qOptions) {
      return (
        <select
          value={String(c.value ?? '')}
          onChange={(e) => {
            const opt = qOptions.find((o) => o.value === e.target.value);
            const next: SearchCondition = { ...c, value: e.target.value };
            if (opt && opt.label !== opt.value) next.valueLabel = opt.label;
            else delete next.valueLabel;
            onChange(next);
          }}
          aria-label="Answer value"
          className={`${fieldCls} w-full cursor-pointer`}
        >
          <option value="">Pick an option…</option>
          {qOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input
        type={numeric ? 'number' : 'text'}
        value={String(c.value ?? '')}
        onChange={(e) => onChange({ ...c, value: e.target.value })}
        placeholder={numeric ? 'e.g. 30' : 'answer'}
        aria-label="Answer value"
        className={`${fieldCls} w-full`}
      />
    );
  };

  const formsAsking = labelInfo ? labelInfo.forms.length : null;
  const linkCls = 'text-xs font-medium text-primary-600 hover:underline dark:text-primary-400';

  return (
    <div className="space-y-1.5">
      {custom ? (
        <>
          <input
            value={c.question || ''}
            onChange={(e) => {
              // Typing takes the wheel: no more automatic reopening on a form. A hand-typed ID has
              // no words of its own until the builder looks it up again.
              autoReopen.current = false;
              const next: SearchCondition = { ...c, question: e.target.value };
              delete next.label;
              delete next.valueLabel;
              delete next.type;
              onChange(next);
            }}
            placeholder="Question ID (e.g. ckd_q_age or form.q_weight)"
            aria-label="Question ID"
            className={`${fieldCls} w-full`}
          />
          <p className={hintCls}>Matches this answer wherever it was given — landing page, prescreen, or texted form.</p>
          <button type="button" onClick={() => { autoReopen.current = false; setCustom(false); }} className={linkCls}>
            Pick the question from a form instead
          </button>
        </>
      ) : (
        <>
          <div className="relative">
            <FormSelect value={pickerForm} onChange={pickForm} forms={forms} staleTitle={c.picker_form_title} />
            {loading && <Loader2 className="pointer-events-none absolute right-7 top-2 h-4 w-4 animate-spin text-gray-400" />}
          </div>
          {pickerForm && (
            <select
              value={c.question || ''}
              onChange={(e) => pickQuestion(e.target.value)}
              aria-label="Question"
              className={`${fieldCls} w-full cursor-pointer`}
              disabled={loading}
            >
              <option value="">{loading ? 'Loading questions…' : 'Pick a question…'}</option>
              {/* A saved question this form no longer lists keeps a readable option. */}
              {c.question && questions && !selected && (
                <option value={c.question}>{c.label || 'A question this form no longer asks'}</option>
              )}
              {(questions || []).map((q) => <option key={q.key} value={q.key}>{q.label || q.key}</option>)}
            </select>
          )}
        </>
      )}
      {!presenceOnly && c.question && (
        <div className={rowCls}>
          <select
            value={c.op || 'eq'}
            onChange={(e) => onChange({ ...c, op: e.target.value, ...(e.target.value === 'answered' ? { value: '' } : {}) })}
            aria-label="Comparison"
            className={`${fieldCls} w-auto shrink-0 cursor-pointer`}
          >
            {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="min-w-0 flex-1">{valueControl()}</div>
        </div>
      )}
      {!custom && pickerForm && (
        <>
          <label className={labelCls}>
            <input
              type="checkbox"
              checked={Boolean(c.form_id)}
              onChange={(e) => onChange({ ...c, form_id: e.target.checked ? pickerForm : undefined })}
              className={checkCls}
            />
            Only people who answered on this form
          </label>
          {!c.form_id && c.question && (
            <p className={hintCls}>
              {formsAsking !== null && formsAsking > 1
                ? `Also counts this question on other forms (${formsAsking} forms ask it).`
                : formsAsking === 1
                  ? 'Also counts this answer wherever else it was given, like a prescreen.'
                  : 'Also counts this question on other forms that ask it.'}
            </p>
          )}
        </>
      )}
      {!custom && (
        <button type="button" onClick={switchToCustomKey} className={linkCls}>
          Advanced: type a question ID
        </button>
      )}
    </div>
  );
}

export default function CriterionEditor({ condition: c, onChange, studies, team, forms }: Props) {
  // Preload the tag vocabulary once so lab tag labels resolve (fire-and-forget).
  useEffect(() => { loadProfileKeys().catch(() => {}); }, []);

  switch (c.kind) {
    case 'stage':
      return (
        <FilterMultiSelect
          values={c.statuses || []}
          onChange={(v) => onChange({ ...c, statuses: v })}
          options={[{ value: '', label: 'Any stage' }, ...LIFECYCLES.map((l) => ({ value: l.value, label: l.label }))]}
          label="Stages"
        />
      );
    case 'stage_ever': {
      const ordinalMode = c.min_ordinal !== undefined && c.min_ordinal !== null && String(c.min_ordinal) !== '';
      const stageLabelOf = (v: string) => LIFECYCLES.find((l) => l.value === v)?.label || v;
      return (
        <div className="space-y-1.5">
          <select
            value={ordinalMode ? 'ordinal' : 'statuses'}
            onChange={(e) => onChange(e.target.value === 'ordinal'
              ? { kind: c.kind, min_ordinal: 5 }
              : { kind: c.kind, statuses: [] })}
            aria-label="History mode"
            className={`${fieldCls} w-full cursor-pointer`}
          >
            <option value="statuses">Was ever in specific stage(s)</option>
            <option value="ordinal">Reached at least a spine stage</option>
          </select>
          {ordinalMode ? (
            <select
              value={String(c.min_ordinal)}
              onChange={(e) => onChange({ ...c, min_ordinal: Number(e.target.value) })}
              aria-label="Minimum spine stage"
              className={`${fieldCls} w-full cursor-pointer`}
            >
              {SPINE_STAGES.map((s) => (
                <option key={s.ordinal} value={s.ordinal}>{stageLabelOf(s.value)} or further</option>
              ))}
            </select>
          ) : (
            <FilterMultiSelect
              values={c.statuses || []}
              onChange={(v) => onChange({ ...c, statuses: v })}
              options={[{ value: '', label: 'Any stage' }, ...LIFECYCLES.map((l) => ({ value: l.value, label: l.label }))]}
              label="Stages ever reached"
            />
          )}
          <p className={hintCls}>History from the stage ledger — catches leads who reached a stage and later moved on or dropped out.</p>
        </div>
      );
    }
    case 'study':
      return (
        <div className="space-y-1.5">
          <FilterMultiSelect
            values={c.slugs || []}
            onChange={(v) => onChange({ ...c, slugs: v })}
            options={[{ value: '', label: 'Any study' }, ...studies.map((s) => ({ value: s.slug, label: `${studyInternalLabel(s)} (${s.slug})` }))]}
            label="Studies"
          />
          <select
            value={c.scope || 'current'}
            onChange={(e) => onChange({ ...c, scope: e.target.value as 'current' | 'ever' })}
            aria-label="Study scope"
            className={`${fieldCls} w-full cursor-pointer`}
          >
            <option value="current">Current study (considering)</option>
            <option value="ever">Ever submitted to</option>
          </select>
        </div>
      );
    case 'no_study':
      return <p className={hintCls}>Leads with no considering study set — the cross-sell pool.</p>;
    case 'owner':
      return (
        <div className="space-y-1.5">
          <FilterMultiSelect
            values={c.user_ids || []}
            onChange={(v) => onChange({ ...c, user_ids: v })}
            options={[{ value: '', label: 'Any owner' }, ...team.map((m) => ({ value: m.id, label: m.name }))]}
            label="Owners"
          />
          <label className={labelCls}>
            <input
              type="checkbox"
              checked={c.include_unassigned === true}
              onChange={(e) => onChange({ ...c, include_unassigned: e.target.checked })}
              className={checkCls}
            />
            Include unassigned
          </label>
        </div>
      );
    case 'incoming_eligibility':
      return (
        <FilterMultiSelect
          values={c.values || []}
          onChange={(v) => onChange({ ...c, values: v })}
          options={[{ value: '', label: 'Any verdict' }, ...INCOMING_ELIGIBILITY_OPTIONS]}
          label="Incoming eligibility"
        />
      );
    case 'milestone':
      return (
        <FilterMultiSelect
          values={c.keys || []}
          onChange={(v) => onChange({ ...c, keys: v })}
          options={[{ value: '', label: 'Any milestone' }, ...MILESTONE_FILTER_OPTIONS.map((k) => ({ value: k as string, label: MILESTONE_LABELS[k] }))]}
          label="Milestones"
        />
      );
    case 'lead_source':
      return (
        <div>
          <input
            value={(c.sources || []).join(', ')}
            onChange={(e) => onChange({ ...c, sources: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            placeholder="fb, ig, an, manual, import"
            aria-label="Lead sources"
            className={`${fieldCls} w-full`}
          />
          <p className={`mt-1 ${hintCls}`}>
            Source codes as they appear on leads, separated by commas — fb (Facebook), ig (Instagram),
            manual (added by hand), import (imported).
          </p>
        </div>
      );
    case 'utm':
      return (
        <div className="space-y-1.5">
          <div className={rowCls}>
            <select
              value={c.field || 'campaign'}
              onChange={(e) => onChange({ ...c, field: e.target.value })}
              aria-label="UTM field"
              className={`${fieldCls} w-auto cursor-pointer`}
            >
              {UTM_FIELD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input
              value={(c.values || []).join(', ')}
              onChange={(e) => onChange({ ...c, values: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
              placeholder="spring-glp1, brand-a"
              aria-label="UTM values"
              className={`${fieldCls} min-w-[10rem] flex-1`}
            />
          </div>
          <p className={hintCls}>
            Exact values, separated by commas. Only people who came in through our own landing pages
            carry ad details — imported or hand-added leads have nothing on file for this.
          </p>
        </div>
      );
    case 'disposition':
      return (
        <div className="space-y-1.5">
          <FilterMultiSelect
            values={c.to_statuses || []}
            onChange={(v) => onChange({ ...c, to_statuses: v })}
            options={[{ value: '', label: 'Any disposition' }, ...DISPOSITION_STATUS_OPTIONS]}
            label="Dispositions"
          />
          {/* Reasons from the picked close-outs (every reason when none is picked); a saved code
              the vocabulary no longer lists keeps its own option so nothing silently drops. */}
          <FilterMultiSelect
            values={c.reason_codes || []}
            onChange={(v) => onChange({ ...c, reason_codes: v })}
            options={[
              { value: '', label: 'Any reason' },
              ...(() => {
                const picked = c.to_statuses || [];
                const pool = picked.length
                  ? picked.flatMap((st) => (st === 'not_eligible'
                    ? DQ_CATEGORY_OPTIONS
                    : (DISPOSITION_REASONS[st] || []).map((r) => ({ value: r.value, label: r.label }))))
                  : DISPOSITION_REASON_OPTIONS;
                const seen = new Set<string>();
                const opts = pool.filter((o) => {
                  if (seen.has(o.value)) return false;
                  seen.add(o.value);
                  return true;
                });
                const stale = (c.reason_codes || []).filter((v) => !seen.has(v)).map((v) => ({
                  value: v,
                  label: DISPOSITION_REASON_OPTIONS.find((o) => o.value === v)?.label || v,
                }));
                return [...opts, ...stale];
              })(),
            ]}
            label="Reasons"
          />
          <FilterMultiSelect
            values={c.study_slugs || []}
            onChange={(v) => onChange({ ...c, study_slugs: v })}
            options={[{ value: '', label: 'Any study' }, ...studies.map((s) => ({ value: s.slug, label: studyInternalLabel(s) }))]}
            label="Studies"
          />
          <div className={rowCls}>
            <span className={hintCls}>between</span>
            <input type="date" value={c.from ? String(c.from).slice(0, 10) : ''} onChange={(e) => onChange({ ...c, from: e.target.value || undefined })} aria-label="Dispositioned from" className={`${fieldCls} w-auto`} />
            <span className={hintCls}>and</span>
            <input type="date" value={c.to ? String(c.to).slice(0, 10) : ''} onChange={(e) => onChange({ ...c, to: e.target.value || undefined })} aria-label="Dispositioned to" className={`${fieldCls} w-auto`} />
          </div>
        </div>
      );
    case 'tag':
      return (
        <div>
          <input
            value={(c.tags || []).join(', ')}
            onChange={(e) => onChange({ ...c, tags: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            placeholder="e.g. crio:completed"
            aria-label="Contact tags"
            className={`${fieldCls} w-full`}
          />
        </div>
      );
    case 'interest':
      return (
        <div>
          <InterestListsSelect condition={c} onChange={onChange} />
          <p className={`mt-1 ${hintCls}`}>
            Leads kept for a standard study type (added on the lead page or at close-out).
          </p>
        </div>
      );
    case 'created':
      return (
        <div className={rowCls}>
          <input type="date" value={c.from ? String(c.from).slice(0, 10) : ''} onChange={(e) => onChange({ ...c, from: e.target.value || '' })} aria-label="Created from" className={`${fieldCls} w-auto`} />
          <span className={hintCls}>to</span>
          <input type="date" value={c.to ? String(c.to).slice(0, 10) : ''} onChange={(e) => onChange({ ...c, to: e.target.value || '' })} aria-label="Created to" className={`${fieldCls} w-auto`} />
        </div>
      );
    case 'activity':
      return (
        <div className={rowCls}>
          <select value={c.field || 'any'} onChange={(e) => onChange({ ...c, field: e.target.value })} aria-label="Activity direction" className={`${fieldCls} w-auto cursor-pointer`}>
            <option value="any">Any activity</option>
            <option value="inbound">Heard from them</option>
            <option value="outbound">We contacted them</option>
          </select>
          <select value={c.op || 'not_within'} onChange={(e) => onChange({ ...c, op: e.target.value })} aria-label="Within or not" className={`${fieldCls} w-auto cursor-pointer`}>
            <option value="within">in the last</option>
            <option value="not_within">not in the last</option>
          </select>
          <input type="number" min={1} value={String(c.days ?? '')} onChange={(e) => onChange({ ...c, days: Number(e.target.value) || 0 })} aria-label="Days" className={`${fieldCls} w-20`} />
          <span className={hintCls}>days</span>
        </div>
      );
    case 'contactable':
      return (
        <select value={c.channel || 'sms'} onChange={(e) => onChange({ ...c, channel: e.target.value })} aria-label="Channel" className={`${fieldCls} w-full cursor-pointer`}>
          <option value="sms">By SMS (has phone, not opted out)</option>
          <option value="email">By email (has address, not suppressed)</option>
        </select>
      );
    case 'snoozed':
      return (
        <select value={c.state || 'snoozed'} onChange={(e) => onChange({ ...c, state: e.target.value })} aria-label="Snooze state" className={`${fieldCls} w-full cursor-pointer`}>
          <option value="snoozed">Currently snoozed</option>
          <option value="not_snoozed">Not snoozed</option>
        </select>
      );
    case 'archived':
      return (
        <div>
          <select value={c.state || 'archived'} onChange={(e) => onChange({ ...c, state: e.target.value })} aria-label="Archived state" className={`${fieldCls} w-full cursor-pointer`}>
            <option value="archived">Archived</option>
            <option value="active">Not archived</option>
          </select>
          <p className={`mt-1 ${hintCls}`}>Only meaningful when the base population includes archived leads.</p>
        </div>
      );
    case 'condition':
      return (
        <TagOrNameEditor
          condition={c}
          onChange={onChange}
          keyPrefixes={['condition.*']}
          statuses={[{ value: 'active', label: 'Active' }, { value: 'resolved', label: 'Resolved' }, { value: 'unknown', label: 'Unknown' }]}
          placeholder="e.g. type 2 diabetes"
        />
      );
    case 'medication':
      return (
        <TagOrNameEditor
          condition={c}
          onChange={onChange}
          keyPrefixes={['med.*']}
          statuses={[{ value: 'active', label: 'Active' }, { value: 'stopped', label: 'Stopped' }, { value: 'unknown', label: 'Unknown' }]}
          placeholder="e.g. semaglutide"
        />
      );
    case 'lab':
      return (
        <div className="space-y-1.5">
          <div className={rowCls}>
            <input
              value={c.test_name || ''}
              onChange={(e) => onChange({ ...c, test_name: e.target.value, tag: '', tag_label: undefined })}
              placeholder="test name (e.g. a1c)"
              aria-label="Lab test"
              className={`${fieldCls} min-w-[8rem] flex-1`}
            />
            <span className={hintCls}>or tag</span>
            <div className="min-w-[10rem] flex-1">
              <ProfileKeyPicker
                value={c.tag || null}
                onChange={async (key) => {
                  if (!key) { onChange({ ...c, tag: '', tag_label: undefined }); return; }
                  let label: string | undefined;
                  try { label = (await loadProfileKeys()).find((k) => k.key === key)?.label; } catch { /* label optional */ }
                  onChange({ ...c, tag: key, tag_label: label, test_name: '' });
                }}
                placeholder="field tag"
              />
            </div>
          </div>
          <NumericValueRow condition={c} onChange={onChange} />
          <LabUnitsHint tag={c.tag} testName={c.test_name} />
        </div>
      );
    case 'vital':
      return (
        <div className="space-y-1.5">
          <select value={c.preset || 'bmi'} onChange={(e) => onChange({ ...c, preset: e.target.value })} aria-label="Vital" className={`${fieldCls} w-full cursor-pointer`}>
            {VITAL_PRESET_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <NumericValueRow condition={c} onChange={onChange} />
        </div>
      );
    case 'demographic':
      return (
        <div className="space-y-1.5">
          <select
            value={c.key || 'age'}
            onChange={(e) => onChange(e.target.value === 'sex'
              ? { kind: c.kind, key: 'sex', values: [] }
              : { kind: c.kind, key: 'age', op: 'between', value: '', value2: '' })}
            aria-label="Demographic"
            className={`${fieldCls} w-full cursor-pointer`}
          >
            <option value="age">Age</option>
            <option value="sex">Sex</option>
          </select>
          {c.key === 'sex' ? (
            <FilterMultiSelect
              values={c.values || []}
              onChange={(v) => onChange({ ...c, values: v })}
              options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }]}
              label="Sex"
            />
          ) : (
            <NumericValueRow condition={c} onChange={onChange} allowRecency={false} />
          )}
          <p className={hintCls}>
            Age and sex here come only from where a prescreen asked or their records showed them —
            the date of birth on the lead header isn't read by this step. Expect a low share of
            people with it on file; that's the truth of the data.
          </p>
        </div>
      );
    case 'form_submitted':
      return (
        <div className="space-y-1.5">
          <FormSelect
            value={c.form_id || ''}
            onChange={(formId) => {
              const f = forms.find((x) => x.id === formId);
              onChange({
                ...c,
                form_id: formId,
                picker_form_id: formId || undefined,
                picker_form_title: f?.title || undefined,
                picker_form_surface: f ? formSurface(f.surface) : undefined,
              });
            }}
            forms={forms}
            staleTitle={c.picker_form_title}
          />
          <label className={labelCls}>
            <input
              type="checkbox"
              checked={c.status === 'any'}
              onChange={(e) => onChange({ ...c, status: e.target.checked ? 'any' : 'completed' })}
              className={checkCls}
            />
            Count people who started it but didn't finish too
          </label>
        </div>
      );
    case 'answer':
      return <AnswerEditor condition={c} onChange={onChange} forms={forms} />;
    case 'answered':
      return <AnswerEditor condition={c} onChange={onChange} forms={forms} presenceOnly />;
    case 'study_eligibility':
      return (
        <div className="space-y-1.5">
          <select
            value={c.study_id || ''}
            onChange={(e) => onChange({ ...c, study_id: e.target.value })}
            aria-label="Study"
            className={`${fieldCls} w-full cursor-pointer`}
          >
            <option value="">Pick a study…</option>
            {studies.filter((s) => s.id).map((s) => <option key={String(s.id)} value={String(s.id)}>{studyInternalLabel(s)}</option>)}
          </select>
          <FilterMultiSelect
            values={c.verdicts || []}
            onChange={(v) => onChange({ ...c, verdicts: v })}
            options={[{ value: '', label: 'Any verdict' }, ...STUDY_ELIGIBILITY_VERDICT_OPTIONS]}
            label="Verdicts"
          />
          <p className={hintCls}>The current prescreen result for that study. People never prescreened for it have nothing on file.</p>
        </div>
      );
    case 'ai_review':
      return (
        <div className="space-y-1.5">
          <select
            value={c.study_id || ''}
            onChange={(e) => onChange({ ...c, study_id: e.target.value || undefined })}
            aria-label="Study"
            className={`${fieldCls} w-full cursor-pointer`}
          >
            <option value="">Any study</option>
            {studies.filter((s) => s.id).map((s) => <option key={String(s.id)} value={String(s.id)}>{studyInternalLabel(s)}</option>)}
          </select>
          <FilterMultiSelect
            values={c.results || []}
            onChange={(v) => onChange({ ...c, results: v })}
            options={[{ value: '', label: 'Any result' }, ...AI_REVIEW_RESULT_OPTIONS]}
            label="Results"
          />
          <label className={labelCls}>
            <input
              type="checkbox"
              checked={c.latest_only !== false}
              onChange={(e) => onChange({ ...c, latest_only: e.target.checked })}
              className={checkCls}
            />
            Latest review per study only
          </label>
        </div>
      );
    case 'dq':
      return (
        <div className="space-y-1.5">
          <FilterMultiSelect
            values={c.categories || []}
            onChange={(v) => onChange({ ...c, categories: v })}
            options={[{ value: '', label: 'Any category' }, ...DQ_CATEGORY_OPTIONS]}
            label="Categories"
          />
          <FilterMultiSelect
            values={c.study_slugs || []}
            onChange={(v) => onChange({ ...c, study_slugs: v })}
            options={[{ value: '', label: 'Any study' }, ...studies.map((s) => ({ value: s.slug, label: studyInternalLabel(s) }))]}
            label="Studies"
          />
          <div className={rowCls}>
            <span className={hintCls}>between</span>
            <input type="date" value={c.from ? String(c.from).slice(0, 10) : ''} onChange={(e) => onChange({ ...c, from: e.target.value || undefined })} aria-label="Disqualified from" className={`${fieldCls} w-auto`} />
            <span className={hintCls}>and</span>
            <input type="date" value={c.to ? String(c.to).slice(0, 10) : ''} onChange={(e) => onChange({ ...c, to: e.target.value || undefined })} aria-label="Disqualified to" className={`${fieldCls} w-auto`} />
          </div>
        </div>
      );
    case 'communication':
      return (
        <div className="space-y-1.5">
          <div className={rowCls}>
            <FilterMultiSelect
              values={c.channels || []}
              onChange={(v) => onChange({ ...c, channels: v.length ? v : ['call'] })}
              options={COMM_CHANNEL_OPTIONS}
              label="Channels"
            />
            <select
              value={c.direction || 'any'}
              onChange={(e) => onChange({ ...c, direction: e.target.value })}
              aria-label="Direction"
              className={`${fieldCls} w-auto cursor-pointer`}
            >
              <option value="any">Either direction</option>
              <option value="inbound">Inbound only</option>
              <option value="outbound">Outbound only</option>
            </select>
          </div>
          <div className={rowCls}>
            <span className={hintCls}>at least</span>
            <input
              type="number" min={1} max={100}
              value={String(c.min_count ?? 1)}
              onChange={(e) => onChange({ ...c, min_count: e.target.value === '' ? 1 : Number(e.target.value) })}
              aria-label="Minimum count"
              className={`${fieldCls} w-20`}
            />
            <span className={hintCls}>within the last</span>
            <input
              type="number" min={1} max={365}
              value={String(c.within_days ?? '')}
              onChange={(e) => onChange({ ...c, within_days: e.target.value === '' ? undefined : Number(e.target.value) })}
              aria-label="Window days"
              className={`${fieldCls} w-20`}
            />
            <span className={hintCls}>days</span>
          </div>
        </div>
      );
    case 'has_records':
      return (
        <FilterMultiSelect
          values={c.doc_types || []}
          onChange={(v) => onChange({ ...c, doc_types: v.length ? v : ['medical_record'] })}
          options={[
            { value: '', label: 'Any type' },
            { value: 'medical_record', label: 'Medical records' },
            { value: 'prescreening_summary', label: 'Prescreening summaries' },
          ]}
          label="Document types"
        />
      );
    default:
      return <p className={hintCls}>This is an older kind of step that no longer exists, so it can't be edited. Remove it and add a new step.</p>;
  }
}
